/**
 * Reviewing an import before anything is filed: what we found in each file, where every column
 * goes, a few animals as they'll look with their history and family, and the rows that need a
 * look. One obvious action at the bottom. After filing: the reconciliation report and undo.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { ANIMAL_STATUS, ENTITY_FIELDS, type Msg } from "../../../contract";
import { api } from "../../../web/lib/api";
import { formatDate, useFormat } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCaptureSubject } from "../../../web/shell/Composer";
import { eventSummary } from "../../../web/shell/describe";
import { Page } from "../../../web/shell/Layout";
import { registry } from "../../../web/registry";
import { Button, Card, Chip, Disclosure, Empty, ErrorNote, Field, Icon, List, Loading, Menu, PageHeader, Progress, Row, Section, Segmented, Select, Sheet, Stat, TagLabel, Toggle, toast } from "../../../web/ui/kit";
import { mappableEventTypes } from "../shared/targets";
import type { ColumnPlan, ImportDTO, InventoryFile, PreviewAnimal, RowPreview, TablePlan, TableFamily } from "../shared/types";
import { fileUrl, tm } from "./api";
import { IssueList } from "./Issues";
import { COLUMN_OUTCOME, FAMILY_LABEL, formatBytes, IMPORT_STATUS, OutcomeChip, ROW_OUTCOME, StatusChip, targetLabel, targetsFor, sourceName } from "./labels";

type Full = ImportDTO & {
  questions?: Msg[];
  restore?: { ranch: string; exported_at: string; counts: Record<string, { total: number; matched: number }>; as_notes: number; finance: string; existing: { animals: number; places: number } };
};

export function ImportReview() {
  useLanguage();
  const { id } = useParams();
  const q = useApi<Full>(`/m/data/imports/${id}`);
  useCaptureSubject(q.data ? { screen: `import:${q.data.id}` } : null);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <Page><ErrorNote error={q.error} retry={() => q.refetch()} /></Page>;
  const imp = q.data;
  const refresh = async () => { await q.refetch(); };
  return (
    <Page>
      <PageHeader back={{ to: "/ranch/data", label: t("Your data") }} eyebrow={t("From {{source}}", { source: sourceName(imp.source.name) })} title={imp.title}
        subtitle={[plural(imp.files.filter((f) => !f.parent_id).length, "{{count}} file", "{{count}} files"), t("Exported {{date}}", { date: formatDate(imp.source_date) })].join(" · ")}
        actions={imp.status === "review" ? <Menu label={t("Import options")} items={[{ label: t("Set aside without filing"), icon: "archive", danger: true, onSelect: () => void api.post(`/m/data/imports/${imp.id}/discard`).then(refresh).then(invalidateAll) }]} /> : undefined}>
        <StatusChip map={IMPORT_STATUS} value={imp.status} />
      </PageHeader>
      {imp.error && <Card tone="warn"><p>{tm(imp.error)}</p></Card>}
      {imp.status === "review" && (imp.restore ? <RestoreReview imp={imp} onChange={refresh} /> : <Review imp={imp} onChange={refresh} />)}
      {imp.status === "committing" && <Committing imp={imp} onChange={refresh} />}
      {(imp.status === "committed" || imp.status === "undone") && <Report imp={imp} onChange={refresh} />}
      {imp.status === "discarded" && <Empty icon="archive" title={t("Put aside")} body={t("Nothing from this file was filed. The original stays here.")} />}
      <Files imp={imp} />
    </Page>
  );
}

// ---------------------------------------------------------------- review

function Review({ imp, onChange }: { imp: Full; onChange: () => Promise<void> }) {
  const [aiBusy, setAiBusy] = useState(false);
  const asked = useRef(false);
  const patch = async (tables: unknown[]) => { await api.patch(`/m/data/imports/${imp.id}`, { tables }); await onChange(); };
  const askAi = async () => {
    setAiBusy(true);
    try { await api.post(`/m/data/imports/${imp.id}/ai-map`); await onChange(); } catch (err) { toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "warn" }); } finally { setAiBusy(false); }
  };
  useEffect(() => {
    if (asked.current || !imp.ai_available || imp.plan_by !== "heuristic" || !imp.plan.tables.some((x) => x.include)) return;
    asked.current = true;
    void askAi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imp.id]);
  const p = imp.preview;
  const tables = imp.plan.tables;
  const animals = p?.counts.animals ?? 0;
  return (
    <div className="stack">
      {aiBusy && <Card><span className="spinner"><Icon name="spinner" className="spin" size={18} />{t("Hondo is reading your columns…")}</span></Card>}
      {(imp.questions ?? []).length > 0 && <DateQuestions imp={imp} patch={patch} />}
      {p && (
        <div className="data-stats">
          <Stat label={t("New")} value={p.counts.create} tone={p.counts.create ? "good" : undefined} />
          <Stat label={t("Updated")} value={p.counts.update} />
          <Stat label={t("No change")} value={p.counts.unchanged} />
          <Stat label={t("Need a look")} value={p.counts.blocked + p.counts.conflict} tone={p.counts.blocked + p.counts.conflict ? "warn" : undefined} />
          {p.counts.events > 0 && <Stat label={t("Records")} value={p.counts.events} hint={p.counts.notes ? plural(p.counts.notes, "{{count}} as a note", "{{count}} as notes") : undefined} />}
          {p.counts.places > 0 && <Stat label={t("New places")} value={p.counts.places} />}
          {p.counts.tasks > 0 && <Stat label={t("Tasks")} value={p.counts.tasks} />}
        </div>
      )}
      {p && (p.counts.former > 0 || p.counts.reference > 0 || p.older_file) && (
        <Card>
          {p.counts.former > 0 && <p>{plural(p.counts.former, "{{count}} sold or dead animal comes in as a former animal, not part of the herd.", "{{count}} sold or dead animals come in as former animals, not part of the herd.")}</p>}
          {p.counts.reference > 0 && <p>{plural(p.counts.reference, "{{count}} ancestor comes in as reference only, outside your head count.", "{{count}} ancestors come in as reference only, outside your head count.")}</p>}
          {p.older_file && <p className="data-warn"><Icon name="warn" size={16} /> {t("This file is older than one you brought in before. It won't overwrite newer records; where it differs, we'll ask.")}</p>}
        </Card>
      )}
      <Section title={t("What we found")} hint={imp.plan_by === "ai" ? t("Hondo matched the columns. Tap any column to change it.") : t("Matched by the column headings. Tap any column to change it.")}
        action={imp.ai_available && !aiBusy ? <Button size="sm" variant="ghost" icon="sparkle" onClick={() => void askAi()}>{t("Have Hondo check")}</Button> : undefined}>
        {tables.length === 0 ? <p className="muted">{t("No lists of records in these files.")}</p> : <div className="stack">{tables.map((table) => <TableCard key={table.id} imp={imp} table={table} patch={patch} />)}</div>}
      </Section>
      {p && p.animals.length > 0 && (
        <Section title={t("How a few will look")}>
          <div className="cards">{p.animals.map((a, i) => <AnimalPreview key={`${a.key}-${i}`} a={a} />)}</div>
        </Section>
      )}
      {p && p.attention.length > 0 && (
        <Section title={t("Needs a look")} count={p.attention.length} hint={t("Everything else can come in. Settle these now, or skip them and bring them in later.")}>
          <div className="stack">{p.attention.map((r) => <AttentionRow key={`${r.table_id}#${r.row}`} imp={imp} row={r} onChange={onChange} />)}</div>
        </Section>
      )}
      {p && p.unmatched_parents.length > 0 && (
        <Section title={t("Parents not in your herd")} hint={t("These calves keep the parent as written. After filing you can link each one or add it as a reference ancestor.")}>
          <List>{p.unmatched_parents.map((u) => <Row key={u.label} icon="help" title={u.label} meta={t("Parent of {{animals}}", { animals: u.children.slice(0, 6).join(", ") })} />)}</List>
        </Section>
      )}
      {p && p.duplicate_tags.length > 0 && (
        <Section title={t("Tags on more than one animal")}>
          <List>{p.duplicate_tags.map((d) => <Row key={d.tag} icon="tag" title={d.tag} meta={d.rows.join(" · ")} />)}</List>
        </Section>
      )}
      <CommitBar imp={imp} animals={animals} onChange={onChange} />
    </div>
  );
}

function DateQuestions({ imp, patch }: { imp: Full; patch: (tables: unknown[]) => Promise<void> }) {
  const cols = imp.plan.tables.flatMap((table) => table.columns.filter((c) => c.outcome === "unverified" && !c.date_order && /birth_date|@date/.test(c.target)).map((c) => ({ table, c })));
  if (!cols.length) return null;
  return (
    <Card tone="warn">
      {cols.map(({ table, c }) => {
        const sample = c.samples.find((s) => /^\d{1,2}[/.-]\d{1,2}[/.-]/.test(s)) ?? c.samples[0] ?? "";
        const [a, b] = sample.split(/[/.-]/).map(Number);
        const y = new Date().getFullYear();
        const md = a && b ? formatDate(`${y}-${String(a).padStart(2, "0")}-${String(b).padStart(2, "0")}`) : "";
        const dm = a && b ? formatDate(`${y}-${String(b).padStart(2, "0")}-${String(a).padStart(2, "0")}`) : "";
        return (
          <div key={`${table.id}:${c.index}`} className="stack">
            <strong>{t("How does {{column}} write dates?", { column: c.header })}</strong>
            <p className="muted">{t("{{example}} could be {{first}} or {{second}}.", { example: sample, first: md, second: dm })}</p>
            <div className="data-choices">
              <Button size="lg" onClick={() => void patch([{ id: table.id, columns: [{ index: c.index, date_order: "mdy" }] }])}>{t("Month first ({{date}})", { date: md })}</Button>
              <Button size="lg" onClick={() => void patch([{ id: table.id, columns: [{ index: c.index, date_order: "dmy" }] }])}>{t("Day first ({{date}})", { date: dm })}</Button>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

const FAMILY_CHOICES: TableFamily[] = ["animals", "places", "events", "unknown"];

function TableCard({ imp, table, patch }: { imp: Full; table: TablePlan; patch: (tables: unknown[]) => Promise<void> }) {
  const [editing, setEditing] = useState<ColumnPlan | null>(null);
  const [peek, setPeek] = useState(false);
  const targets = targetsFor(table.family, table.event_type);
  const v1 = table.family.startsWith("v1_");
  const eventTypes = mappableEventTypes(registry);
  const familyValue = table.family === "events" ? `events:${table.event_type}` : table.family;
  return (
    <Card className="data-table-card">
      <div className="data-table-head">
        <span className="data-table-title"><Icon name="file" size={16} /> {table.file_name}{table.sheet ? ` · ${table.sheet}` : ""}{table.hidden ? ` · ${t("hidden sheet")}` : ""}</span>
        <span className="muted">{plural(table.data_rows, "{{count}} row", "{{count}} rows")}</span>
      </div>
      {table.note && <p className="muted">{tm(table.note)}</p>}
      {v1 ? <Chip tone="info" icon="data">{t(FAMILY_LABEL[table.family])}</Chip> : (
        <Field label={t("Each row is")}>
          <Select value={familyValue} onChange={(e) => {
            const [family, type] = e.target.value.split(":");
            void patch([{ id: table.id, family, event_type: type ?? null }]);
          }}>
            {FAMILY_CHOICES.filter((f) => f !== "events").map((f) => <option key={f} value={f}>{t(f === "animals" ? "An animal" : f === "places" ? "A place" : "Not sure, leave it in the file")}</option>)}
            {eventTypes.map((e) => <option key={e.type} value={`events:${e.type}`}>{t("A record: {{type}}", { type: t(e.label) })}</option>)}
          </Select>
        </Field>
      )}
      {table.family !== "unknown" && !v1 && <Toggle checked={table.include} onChange={(v) => void patch([{ id: table.id, include: v }])} label={t("Bring this in")} />}
      {table.columns.length > 0 && table.family !== "unknown" && !["v1_records", "v1_tasks"].includes(table.family) && (
        <Disclosure label={t("Columns")} count={table.columns.length} defaultOpen={table.columns.some((c) => c.outcome === "unverified")}>
          <ul className="data-columns">
            {table.columns.map((c) => {
              const target = targets.find((x) => x.id === c.target);
              return (
                <li key={c.index} className="data-column">
                  <button type="button" className="data-column-main" onClick={() => setEditing(c)} aria-label={t("Change where {{column}} goes", { column: c.header })}>
                    <span className="data-column-header">{c.header}</span>
                    <span className="data-column-target"><Icon name="forward" size={14} /> {targetLabel(target, c.target)}{c.date_order ? ` · ${t(c.date_order === "dmy" ? "day first" : c.date_order === "mdy" ? "month first" : "year first")}` : ""}{c.unit ? ` · ${c.unit}` : ""}</span>
                  </button>
                  <OutcomeChip outcome={c.outcome} />
                  {c.samples.length > 0 && <span className="data-column-samples">{c.samples.join(" · ")}</span>}
                  {c.note && <span className="data-column-note">{tm(c.note)}</span>}
                </li>
              );
            })}
          </ul>
        </Disclosure>
      )}
      <div className="row-actions"><Button size="sm" variant="ghost" icon="eye" onClick={() => setPeek(true)}>{t("See the first rows")}</Button></div>
      {editing && <ColumnSheet table={table} column={editing} onClose={() => setEditing(null)} patch={patch} />}
      {peek && <SheetPeek imp={imp} table={table} onClose={() => setPeek(false)} patch={patch} />}
    </Card>
  );
}

function ColumnSheet({ table, column, onClose, patch }: { table: TablePlan; column: ColumnPlan; onClose: () => void; patch: (tables: unknown[]) => Promise<void> }) {
  const targets = targetsFor(table.family, table.event_type);
  const [target, setTarget] = useState(column.target);
  const [order, setOrder] = useState(column.date_order ?? "");
  const [unit, setUnit] = useState(column.unit ?? "");
  const [busy, setBusy] = useState(false);
  const chosen = targets.find((x) => x.id === target);
  const groups: Array<[string, string]> = [["identity", "About the animal"], ["family", "Family"], ["where", "Where"], ["place", "About the place"], ["attributes", "Other details"], ["event", "Records"], ["other", "Other"]];
  const save = async () => {
    setBusy(true);
    try {
      await patch([{ id: table.id, columns: [{ index: column.index, ...(target !== column.target ? { target } : {}), ...(order ? { date_order: order } : {}), ...(unit ? { unit } : {}) }] }]);
      onClose();
    } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={column.header} footer={<Button variant="primary" size="lg" block busy={busy} onClick={() => void save()}>{t("Save")}</Button>}>
      {column.samples.length > 0 && <p className="muted mono">{column.samples.join(" · ")}</p>}
      <Field label={t("Goes to")}>
        <Select value={target} onChange={(e) => { setTarget(e.target.value); setOrder(""); setUnit(""); }}>
          {groups.map(([g, label]) => {
            const options = targets.filter((x) => x.group === g);
            if (!options.length) return null;
            return <optgroup key={g} label={t(label)}>{options.map((x) => <option key={x.id} value={x.id}>{targetLabel(x, x.id)}</option>)}</optgroup>;
          })}
        </Select>
      </Field>
      {chosen && <p className="muted">{t(COLUMN_OUTCOME[chosen.kind === "keep" ? "preserved" : chosen.kind === "skip" ? "unsupported" : chosen.exact ? "exact" : "translated"].label)}: {chosen.kind === "keep" ? t("Kept on each animal under its own heading, as written.") : chosen.kind === "skip" ? t("Stays in your original file only.") : chosen.exact ? t("Stored exactly as written.") : t("Read and converted; anything we can't read is kept as written.")}</p>}
      {chosen?.kind === "date" && (
        <Field label={t("Dates are written")}>
          <Segmented label={t("Dates are written")} value={order || "mdy"} onChange={(v) => setOrder(v)} options={[{ value: "mdy", label: t("Month first") }, { value: "dmy", label: t("Day first") }, { value: "ymd", label: t("Year first") }]} />
        </Field>
      )}
      {chosen?.kind === "quantity" && (
        <Field label={t("Unit")} hint={t("Only when the cells don't say. We never guess pounds or kilograms.")} optional>
          <Select value={unit} onChange={(e) => setUnit(e.target.value)}>
            <option value="">{t("From the cells")}</option>
            {(chosen.units ?? []).map((u) => <option key={u} value={u}>{u}</option>)}
          </Select>
        </Field>
      )}
    </Sheet>
  );
}

function SheetPeek({ imp, table, onClose, patch }: { imp: Full; table: TablePlan; onClose: () => void; patch: (tables: unknown[]) => Promise<void> }) {
  const q = useApi<{ rows: string[][] }>(`/m/data/imports/${imp.id}/sheet/${encodeURIComponent(table.id)}`);
  return (
    <Sheet open onClose={onClose} title={table.sheet ?? table.file_name} size="lg">
      <p className="muted">{t("Tap the row that has the column headings.")}</p>
      {q.isLoading ? <Loading /> : (
        <div className="data-sheet">
          <table><tbody>
            {(q.data?.rows ?? []).map((r, i) => (
              <tr key={i} className={i === table.header_row ? "is-header" : undefined}>
                <td><button type="button" onClick={() => void patch([{ id: table.id, header_row: i }]).then(onClose)} aria-label={t("Headings are on row {{row}}", { row: i + 1 })}>{i + 1}</button></td>
                {r.map((c, j) => <td key={j}>{c}</td>)}
              </tr>
            ))}
          </tbody></table>
        </div>
      )}
    </Sheet>
  );
}

/** A changed field in plain words: "Breed", "Pasture or pen", or a kept heading as written. */
function fieldName(field: string): string {
  if (field.startsWith("attrs.")) return field.slice(6);
  if (field === "place") return t("Pasture or pen");
  if (field === "group") return t("Group");
  const def = ENTITY_FIELDS.animal[field as keyof typeof ENTITY_FIELDS.animal];
  return def ? t(def.label) : field;
}

function AnimalPreview({ a }: { a: PreviewAnimal }) {
  const fmt = useFormat();
  const found = (f: "file" | "herd" | "missing") => t(f === "file" ? "in this file" : f === "herd" ? "in your herd" : "not found");
  return (
    <Card className="data-animal">
      <div className="data-animal-head">
        <TagLabel tag={a.label} size="lg" />
        <Chip tone={ROW_OUTCOME[a.outcome].tone}>{t(ROW_OUTCOME[a.outcome].label)}</Chip>
        {a.status !== "active" && <Chip tone="neutral">{t(ANIMAL_STATUS[a.status as keyof typeof ANIMAL_STATUS] ?? a.status)}</Chip>}
      </div>
      <p className="muted">{[t(a.sex === "female" ? "Female" : a.sex === "male" ? "Male" : "Unknown sex"), a.birth_date ? t("born {{date}}", { date: formatDate(a.birth_date) }) : null, a.breed, a.place, a.group].filter(Boolean).join(" · ")}</p>
      {(a.dam || a.sire) && (
        <p>{a.dam && <>{t("Dam")}: <strong>{a.dam.label}</strong> ({found(a.dam.found)}) </>}{a.sire && <>{t("Sire")}: <strong>{a.sire.label}</strong> ({found(a.sire.found)})</>}</p>
      )}
      {a.changes.length > 0 && (
        <ul>{a.changes.map((c) => <li key={c.field} className={c.conflict ? "data-warn" : undefined}>{c.conflict ? t("{{field}}: yours says {{from}}, the file says {{to}}. We'll ask.", { field: fieldName(c.field), from: c.from ?? "", to: c.to ?? "" }) : t("{{field}}: {{from}} → {{to}}", { field: fieldName(c.field), from: c.from ?? t("blank"), to: c.to ?? "" })}</li>)}</ul>
      )}
      {a.history.length > 0 && (
        <div><strong>{t("History")}</strong><ul>{a.history.map((e, i) => {
          const def = registry.event(e.type);
          return <li key={i}>{e.occurred ? formatDate(e.occurred, e.precision) : t("Unknown date")}: {def ? t(def.label) : e.type}{" · "}{eventSummary({ type: e.type, data: e.data, subjects: e.subjects, occurred_at: e.occurred }, fmt)}{e.as_note ? ` (${t("kept as a note")})` : ""}</li>;
        })}</ul></div>
      )}
      {a.kept.length > 0 && (
        <dl className="data-kept">{a.kept.slice(0, 6).map((k) => <div key={k.header}><dt>{k.header}</dt><dd>{k.value} · {t("kept as written")}</dd></div>)}</dl>
      )}
    </Card>
  );
}

function AttentionRow({ imp, row, onChange }: { imp: Full; row: RowPreview; onChange: () => Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const decision = imp.decisions[`${row.table_id}#${row.row}`];
  const decide = async (action: string, target_id?: string) => {
    setBusy(action + (target_id ?? ""));
    try { await api.post(`/m/data/imports/${imp.id}/rows`, { table_id: row.table_id, row: row.row, action, target_id }); await onChange(); } finally { setBusy(null); }
  };
  const table = imp.plan.tables.find((x) => x.id === row.table_id);
  return (
    <Card className="data-row-card">
      <div className="data-animal-head">
        <TagLabel tag={row.label} />
        <Chip tone={ROW_OUTCOME[row.outcome].tone}>{t(ROW_OUTCOME[row.outcome].label)}</Chip>
        <span className="muted">{t("{{file}}, row {{row}}", { file: table?.sheet ?? table?.file_name ?? "", row: row.row })}</span>
      </div>
      {row.problems.length > 0 && <ul className="data-problems">{row.problems.map((m, i) => <li key={i}>{tm(m)}</li>)}</ul>}
      {row.warnings.length > 0 && <ul className="data-problems data-warn">{row.warnings.map((m, i) => <li key={i}>{tm(m)}</li>)}</ul>}
      {row.candidates && row.candidates.length > 0 && (
        <div className="data-choices">
          {row.candidates.map((c) => <Button key={c.id} busy={busy === `match${c.id}`} onClick={() => void decide("match", c.id)}>{t("It's {{animal}}", { animal: c.label })}</Button>)}
          <Button busy={busy === "matchnew"} onClick={() => void decide("match", "new")}>{t("A different animal")}</Button>
        </div>
      )}
      <div className="data-row-actions">
        {row.outcome === "blocked" && !row.candidates?.length && row.problems.some((m) => /No animal with tag|Two rows use ID/.test(m.text)) && (
          <Button busy={busy === "include"} onClick={() => void decide("include")}>{row.problems.some((m) => /No animal with tag/.test(m.text)) ? t("Add it as a new animal") : t("Bring this row in")}</Button>
        )}
        <Button variant="ghost" busy={busy === "skip"} onClick={() => void decide("skip")}>{t("Skip this row")}</Button>
        {decision && <Button variant="ghost" busy={busy === "clear"} onClick={() => void decide("clear")}>{t("Undo my choice")}</Button>}
      </div>
    </Card>
  );
}

function CommitBar({ imp, animals, onChange }: { imp: Full; animals: number; onChange: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const p = imp.preview;
  const questions = imp.questions ?? [];
  const nothing = !p || (p.counts.create + p.counts.update + p.counts.conflict + p.counts.events + p.counts.tasks === 0 && p.counts.places === 0);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      for (let i = 0; i < 400; i++) {
        const r = await api.post<{ done: boolean; progress: number; total: number }>(`/m/data/imports/${imp.id}/commit`);
        setProgress({ done: r.progress, total: r.total });
        if (r.done) break;
      }
      toast(t("Filed. Everything is in your records."), { tone: "good" });
      await invalidateAll();
      await onChange();
    } catch (err) { setError(err); await onChange(); } finally { setBusy(false); }
  };
  const label = animals ? plural(animals, "Bring in {{count}} animal", "Bring in {{count}} animals") : t("Bring it in");
  return (
    <div className="data-sticky data-noprint">
      {progress && <Progress done={progress.done} total={progress.total} label={t("Filing")} />}
      <Button variant="primary" size="lg" icon="check" busy={busy} disabled={nothing || questions.length > 0} onClick={() => void go()}>{label}</Button>
      {questions.length > 0 && <span className="data-why">{tm(questions[0])}</span>}
      {nothing && !questions.length && <span className="data-why">{t("Nothing to bring in yet. Pick what each sheet is, or turn one on.")}</span>}
      {!nothing && !questions.length && p && p.counts.blocked > 0 && <span className="data-why">{plural(p.counts.blocked, "{{count}} row that needs a look stays out until you settle it.", "{{count}} rows that need a look stay out until you settle them.")}</span>}
      {error ? <ErrorNote error={error} /> : null}
    </div>
  );
}

function Committing({ imp, onChange }: { imp: Full; onChange: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const resume = async () => {
    setBusy(true);
    try {
      for (let i = 0; i < 400; i++) { const r = await api.post<{ done: boolean }>(`/m/data/imports/${imp.id}/commit`); if (r.done) break; }
      await invalidateAll();
      await onChange();
    } finally { setBusy(false); }
  };
  return (
    <Card>
      <p>{t("This import stopped partway. Picking it up again won't duplicate anything already filed.")}</p>
      <Progress done={imp.progress.done} total={Math.max(imp.progress.done, imp.progress.total)} />
      <div className="row-actions"><Button variant="primary" size="lg" busy={busy} onClick={() => void resume()}>{t("Keep going")}</Button></div>
    </Card>
  );
}

// ---------------------------------------------------------------- after filing

function Report({ imp, onChange }: { imp: Full; onChange: () => Promise<void> }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const r = imp.report;
  const undo = async () => {
    setBusy(true);
    setError(null);
    try { await api.post(`/m/data/imports/${imp.id}/undo`); toast(t("Import undone."), { tone: "good" }); setConfirm(false); await invalidateAll(); await onChange(); } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <div className="stack">
      {r && (
        <>
          <div className="data-stats">
            <Stat label={t("Rows read")} value={r.counts.rows} />
            <Stat label={t("Animals added")} value={r.created.animals} tone={r.created.animals ? "good" : undefined} />
            <Stat label={t("Updated")} value={r.counts.update} />
            <Stat label={t("Records filed")} value={r.created.events} hint={r.created.notes ? plural(r.created.notes, "{{count}} as a note", "{{count}} as notes") : undefined} />
            {r.created.places > 0 && <Stat label={t("Places added")} value={r.created.places} />}
            {r.created.tasks > 0 && <Stat label={t("Tasks added")} value={r.created.tasks} />}
          </div>
          <Card>
            <h3>{t("Every row accounted for")}</h3>
            <div className="data-outcomes">
              {(Object.keys(ROW_OUTCOME) as Array<keyof typeof ROW_OUTCOME>).filter((k) => r.counts[k]).map((k) => <Chip key={k} tone={ROW_OUTCOME[k].tone}>{t(ROW_OUTCOME[k].label)}: {r.counts[k]}</Chip>)}
            </div>
            {Object.values(r.columns).some(Boolean) && <>
              <h3>{t("Every column accounted for")}</h3>
              <div className="data-outcomes">{(Object.keys(COLUMN_OUTCOME) as Array<keyof typeof COLUMN_OUTCOME>).filter((k) => r.columns[k]).map((k) => <Chip key={k} tone={COLUMN_OUTCOME[k].tone} icon={COLUMN_OUTCOME[k].icon}>{t(COLUMN_OUTCOME[k].label)}: {r.columns[k]}</Chip>)}</div>
            </>}
            {r.undone && <p className="muted">{t("Undone: {{animals}} animals removed and {{events}} records taken back.", { animals: r.undone.animals, events: r.undone.events })}</p>}
          </Card>
        </>
      )}
      {imp.status === "committed" && imp.open_issues > 0 && <Section title={t("Questions from this import")} count={imp.open_issues}><IssueList importId={imp.id} /></Section>}
      {imp.status === "committed" && imp.can_undo && (
        <Card>
          {imp.can_undo.ok ? (
            <>
              <p>{t("Changed your mind? Undo takes back everything this import added. Nobody has built on it yet.")}</p>
              <div className="row-actions"><Button variant="danger" icon="undo" onClick={() => setConfirm(true)}>{t("Undo this import")}</Button></div>
            </>
          ) : (
            <>
              <p>{t("This import can't be undone anymore:")}</p>
              <ul className="data-problems">{imp.can_undo.reasons.map((m, i) => <li key={i}>{tm(m)}</li>)}</ul>
            </>
          )}
        </Card>
      )}
      <Sheet open={confirm} onClose={() => setConfirm(false)} title={t("Undo this import?")} footer={<Button variant="danger" size="lg" block busy={busy} onClick={() => void undo()}>{t("Undo it")}</Button>}>
        <p>{t("Records it filed are taken back (they stay in history as removed), animals and places it added are removed, and anything it changed goes back to how it was. The original file stays here.")}</p>
        {error ? <ErrorNote error={error} /> : null}
      </Sheet>
    </div>
  );
}

function RestoreReview({ imp, onChange }: { imp: Full; onChange: () => Promise<void> }) {
  const r = imp.restore!;
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const LABELS: Record<string, string> = { place: "Places", group: "Groups", contact: "People and businesses", asset: "Equipment", stock_item: "Supplies", stock_lot: "Supply lots", stock_move: "Supply ins and outs", animal: "Cattle", membership: "Group membership history", event: "Records", job: "Jobs", job_item: "Job worklists", task: "Tasks", fin_doc: "Financial records" };
  const total = Object.values(r.counts).reduce((n, c) => n + c.total - c.matched, 0);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      for (let i = 0; i < 800; i++) { const s = await api.post<{ done: boolean; progress: number; total: number }>(`/m/data/imports/${imp.id}/commit`); setProgress({ done: s.progress, total: s.total }); if (s.done) break; }
      toast(t("Restored."), { tone: "good" });
      await invalidateAll();
      await onChange();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <div className="stack">
      <Card>
        <h2>{t("Restore a backup of {{ranch}}", { ranch: r.ranch })}</h2>
        <p className="muted">{t("Downloaded {{date}}.", { date: formatDate(r.exported_at.slice(0, 10)) })}</p>
        {(r.existing.animals > 0 || r.existing.places > 0) && <p className="data-warn"><Icon name="warn" size={16} /> {t("This ranch already has {{animals}} animals and {{places}} places. The backup's records are added alongside them, not merged.", { animals: r.existing.animals, places: r.existing.places })}</p>}
        {r.as_notes > 0 && <p>{plural(r.as_notes, "{{count}} record is of a kind this Hondo doesn't have; it comes in as a note with every detail.", "{{count}} records are of kinds this Hondo doesn't have; they come in as notes with every detail.")}</p>}
        {r.finance === "not_allowed" && <p>{t("Financial records are in the backup, but only someone who can change finances can restore them.")}</p>}
      </Card>
      <List>{Object.entries(r.counts).map(([k, c]) => <Row key={k} icon="data" title={t(LABELS[k] ?? k)} meta={c.matched ? t("{{total}} in the backup, {{matched}} already here", { total: c.total, matched: c.matched }) : t("{{total}} in the backup", { total: c.total })} />)}</List>
      <div className="data-sticky">
        {progress && <Progress done={progress.done} total={progress.total} />}
        <Button variant="primary" size="lg" icon="check" busy={busy} disabled={!total} onClick={() => void go()}>{plural(total, "Restore {{count}} record", "Restore {{count}} records")}</Button>
        {error ? <ErrorNote error={error} /> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- the original files

function Files({ imp }: { imp: Full }) {
  const [sent, setSent] = useState<Record<string, boolean>>({});
  const send = async (f: InventoryFile) => {
    try {
      await api.post(`/m/data/imports/${imp.id}/files/${f.id}/send`);
      setSent((s) => ({ ...s, [f.id]: true }));
      toast(t("Sent to Hondo. Look for it in Needs a look."), { tone: "good" });
      await invalidateAll();
    } catch (err) { toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "warn" }); }
  };
  const kindLabel = (f: InventoryFile) => t(f.kind === "table" ? "Spreadsheet" : f.kind === "archive" ? "Archive" : f.kind === "document" ? "Document" : f.kind === "image" ? "Photo" : f.kind === "pdf" ? "PDF" : f.kind === "text" ? "Notes" : f.kind === "json" ? "Data file" : f.kind === "backup" ? "Hondo backup" : "Other file");
  return (
    <Section title={t("Your original files")} hint={t("Kept exactly as uploaded, with a fingerprint so you can prove what came in.")}>
      <ul className="data-files">
        {imp.files.map((f) => (
          <li key={f.id} className="data-file">
            <Icon name={f.kind === "image" ? "image" : f.kind === "archive" ? "boxes" : "file"} size={18} />
            <span className="data-file-name">{f.parent_id ? `↳ ${f.path.split("/").slice(1).join("/")}` : f.name}<small>{[kindLabel(f), formatBytes(f.bytes), f.problem ? tm(f.problem) : null].filter(Boolean).join(" · ")}</small></span>
            {(f.status === "failed" || f.status === "unsupported" || f.status === "empty") && <Chip tone="warn" icon="warn">{t(f.status === "empty" ? "Empty" : f.status === "failed" ? "Couldn't read" : "Can't read")}</Chip>}
            {(f.kind === "image" || f.has_text) && imp.status !== "discarded" && <Button size="sm" icon="send" disabled={sent[f.id]} onClick={() => void send(f)}>{sent[f.id] ? t("Sent") : t("Send to Hondo")}</Button>}
            <a className="btn btn-ghost btn-sm" href={fileUrl(`/imports/${imp.id}/files/${f.id}`)} aria-label={t("Download {{file}}", { file: f.name })}><Icon name="download" size={16} /></a>
          </li>
        ))}
      </ul>
      {imp.status === "committed" && <p className="muted"><Link to={`/history`}>{t("See everything in the ranch log")}</Link></p>}
    </Section>
  );
}
